<template>
    <div ref="trWrapper" class="tr-wrapper" :class="{
      'active': (source.id == activeRowId) || (source.isActive), // NB: prefer passing activeRowId over isActive in computed tableData for performance reasons
      'tr-sectionhead': source.isHeading,
      'tr-head': source.isHeaders,
      'tr-empty': source.isEmpty,
      'tr-content': !source.isHeading && !source.isHeaders && !source.isEmpty,
      'tr-disable-hover': source.disableHover
    }">
        <!-- Section Heading -->
        <div class="tr-sectionhead-bar" v-if="source.isHeading" :id="source.id" :class="{'has-section-statuses': hasSectionStatusSlot}">
            <div class="tr-sectionhead-sticky" :style="{'width': stickyWidth + 'px'}">
                <div class="tr-sectionhead-title-wrapper">
                    <a href class="btn pl-0 collapse-trigger" :class="[hasSectionStatusSlot ? 'py-0 d-flex align-items-center' : 'd-inline', {'is-expanded': !source.isClosed}]" role="button" aria-expanded="true" @click.prevent="toggleSection">
                      <collapse-toggle-button />
                      <span class="tr-sectionhead-title">
                        <h5 class="m-0">{{ source.title }} <span class="text-regular">({{ source.count }})</span></h5>
                      </span>
                      <slot name="section-status" :source="source" />
                    </a>
                </div>

                <div class="tr-sectionhead-menu" v-if="source.useDateFilters">
                    <date-filter-trigger :date-filter="source.dateFilter" @click="onDateFilterClick" />
                </div>
            </div>
        </div>
        <!-- Column Headers -->
        <div class="tr" v-if="source.isHeaders && !source.isClosed" >
            <div class="td anchor-col col-sortable" @click.prevent="onSort(headers[0].id)" :class="{'col-sort-asc': (headers[0].id == sortColumnId && sortIsAsc), 'col-sort-desc': (headers[0].id == sortColumnId && !sortIsAsc), 'has-checkbox': hasRowSelect}">
                <label class="row-checkbox" v-if="hasRowSelect"><input type="checkbox" aria-label="Select All" @click.prevent.stop="toggleSelectAll"></label>
                <div class="cell-content">{{ headers[0].label }} <span class="sort-icon" v-show="sortColumnId !== null"></span></div>
            </div>
            <!-- <VueDraggable v-model="draggableHeaders" @end="onHeaderRearrange"> -->
                <div v-for="header in draggableHeaders" @click.prevent="onSort(header.id)" class="td col-sortable draggable" :class="[header.classes, (sortColumnId == header.id && sortIsAsc) ? 'col-sort-asc' : '', (sortColumnId == header.id && !sortIsAsc) ? 'col-sort-desc' : '']" :key="header.id">
                    <div class="cell-content">{{ header.label }} <span class="sort-icon" v-show="sortColumnId !== null"></span></div>
                </div>
            <!-- </VueDraggable> -->
        </div>

        <div class="tr-empty-sticky" v-if="source.isEmpty">
            <em class="text-muted" v-if="!(searchIsActive || filterIsActive)">This section is empty.</em>
            <em class="text-muted" v-if="searchIsActive || filterIsActive">This section has no matching results.</em>
        </div>

        <div class="tr" v-if="!source.isHeading && !source.isHeaders && !source.isEmpty" @click.prevent="onClick">
            <div class="td anchor-col" :class="{ 'has-checkbox': hasRowSelect || hasRowRadioSelect }">
                <label class="row-checkbox" v-if="hasRowSelect"><input type="checkbox" aria-label="Select Row" @click.stop="onSelect" :checked="source.isSelected" :disabled="source.selectDisabled"></label>
                <label class="row-checkbox" v-if="hasRowRadioSelect"><input type="radio" aria-label="Select Row" @click.stop="onRadioRowSelect" :checked="source.isSelected" :disabled="source.selectDisabled"></label>
                <row-actions-menu v-if="source.actionsMenuItems" :items="source.actionsMenuItems" />
                <cell :cell="source.cells[0]" :header="headers[0]" @click.prevent.stop="onCellClick($event, 0)" :class="{'pl-4': source.actionsMenuItems}"/>
            </div>

            <div v-for="(header, index) in headers.slice(1)" :class="['td', header.classes]">
                <cell :cell="source.cells[index + 1]" :header="headers[index + 1]" :input-disabled="source.inputsDisabled" @click.prevent.stop="onCellClick($event, index + 1)" @blur="onCellBlur" @input="onCellInput"/>
            </div>
        </div>

        <template v-if="source.subrows && !source.isHeading && !source.isHeaders  && !source.isEmpty">
            <div class="tr tr-subrow" v-show="!source.isClosed" v-for="subrow in source.subrows" @click.prevent="onClick">
                <div class="td anchor-col"><div class="cell-content"></div>
                    <cell v-if="source.showSubrowsAnchorCol" :cell="subrow[0]" :header="headers[0]" @click.prevent.stop="onCellClick($event, 0)"/>
                </div>

                <div v-for="(header, index) in headers.slice(1)" :class="['td', header.classes, {'td-empty': !((subrow[index + 1] && subrow[index + 1]._meta) ? subrow[index + 1].value : subrow[index + 1]) }]">
                    <cell :cell="subrow[index + 1]" :header="headers[index + 1]" :input-disabled="source.inputsDisabled" @click.prevent.stop="onCellClick($event, index + 1)" @blur="onCellBlur" @input="onCellInput"/>
                </div>
            </div>
        </template>

    </div>
</template>

<script>
import Cell from './Cell'
import DateFilterTrigger from './DateFilterTrigger'
import RowActionsMenu from './RowActionsMenu'
import { VueDraggable } from 'vue-draggable-plus'

export default {
    props: ['source', 'headers', 'onclick', 'onBlur', 'onselect', 'onRadioSelect', 'onSort', 'onToggleSection', 'onToggleSelectAll', 'sortColumnId', 'sortIsAsc', 'stickyWidth', 'dateFilterClickHandler', 'onColumnSort', 'searchIsActive', 'filterIsActive', 'hasRowSelect', 'hasRowRadioSelect', 'activeRowId'],
    components: {Cell, DateFilterTrigger, RowActionsMenu, VueDraggable},
    emits: ['click'],
    data() {
        return {
            draggableHeaders: this.headers.slice(1),
        }
    },
    computed: {
        hasSectionStatusSlot() {
            return Boolean(this.$slots['section-status'])
        },
    },
    watch: {
        headers() {
            this.draggableHeaders = this.headers.slice(1)
        },
    },
    methods: {
        onDateFilterClick() {
            this.dateFilterClickHandler(this.source.id)
        },
        onCellClick($event, columnIdx) {
            this.onclick({
                row: this.source,
                columnIdx: columnIdx,
            })
        },
        onClick() {
            this.onclick({
                row: this.source,
                columnIdx: null,
            })
        },
        onCellInput() {
            // currently only onBlur is necessary so this is a dead-end for the event but could be plumbed through to the top level if necessary
        },
        onCellBlur($event) {
            if (this.onBlur) {
                this.onBlur({
                    row: this.source,
                    value: $event.value,
                    field: $event.field,
                    cell_meta: $event.cell_meta,
                })
            }
        },
        toggleSection() {
            this.onToggleSection(this.source.id)
        },
        onHeaderRearrange() {
            const ids = [this.headers[0].id].concat(this.draggableHeaders.map(h => h.id))
            this.onColumnSort(ids)
        },
        toggleSelectAll() {
            this.onToggleSelectAll(this.source.id)
        },
        onSelect() {
            this.onselect(this.source)
        },
        onRadioRowSelect() {
            this.onRadioSelect(this.source)
        },
    },
}
</script>
